import { useState, useEffect } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import {
  useTimelineShareMutation,
  useLazyGetShareTimelineConnectionsQuery,
  timelineApiSlice,
} from "../../features/timeline/timelineApiSlice";
import {
  useLazyGetBusinessTypeRulesByTypeQuery,
  useCreateTimelineMutation,
} from "../../features/shop/shopApiSlice";
import { apiSlice } from "../../features/api/apiSlice";
import { useGetAllConnectedQuery } from "../../features/connection/connectionApiSlice";
import { FailedNotify, SuccessNotify } from "../../constants/tostify";

/** Returns true only when the current session is an active marketer session */
const isActualMarketer = () => {
  // /user-profile Share must NEVER use marketer leftovers from localStorage —
  // otherwise "Share on → Timeline" only lists marketer connections.
  if (typeof window !== "undefined") {
    const path = window.location.pathname || "";
    if (path === "/user-profile" || path.endsWith("/user-profile")) {
      return false;
    }
  }
  const mid = localStorage.getItem("MeAsmarketerId");
  const prev = localStorage.getItem("previousRoute");
  const isMPath =
    typeof window !== "undefined" &&
    (window.location.pathname === "/marketing-profile" ||
      window.location.pathname.startsWith("/marketing"));
  return (
    isMPath ||
    (!!mid &&
      mid !== "undefined" &&
      mid !== "null" &&
      (prev === "marketing-clients" || prev === "marketing-profile"))
  );
};

const formatBtLabel = (bt) => {
  const raw = String(bt || "generalPublic");
  return raw.charAt(0).toUpperCase() + raw.slice(1).replace(/([A-Z])/g, " $1");
};

export const useShareOption = ({
  mybusinessType,
  sharetoOthers,
  originShopId,
  timelineid,
  postId,
  id,
  handleCloseshare,
  refetchTimeline,
  onShareSuccess,
}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const nameUser = useSelector((state) => state.user?.name);
  const authUser = useSelector((state) => state.auth?.user);
  const currentUser =
    (nameUser?._id ? nameUser : null) || (authUser?._id ? authUser : null);
  const currentUserId = currentUser?._id || nameUser?._id || authUser?._id;

  const defaultGeneralPublic = [
    {
      id: 1,
      label: "General Public",
      value: "generalPublic",
      isSelected: false,
    },
  ];
  const [checkboxes, setCheckboxes] = useState(defaultGeneralPublic);
  const [isShareDisabled, setIsShareDisabled] = useState(true);
  const [modalShow, setModalShow] = useState(false);
  const [listsharetoother, setListsharetoother] =
    useState(defaultGeneralPublic);
  const [postIdMar, setPostIdMar] = useState("");
  const [timelineIdMar, setTimelineIdMar] = useState("");

  const [timelineShareMutation, { isLoading: isSharing }] =
    useTimelineShareMutation();
  const [createTimeline] = useCreateTimelineMutation();
  const [fetchBusinessTypeRules] = useLazyGetBusinessTypeRulesByTypeQuery();
  const [fetchShareConnections] = useLazyGetShareTimelineConnectionsQuery();

  const marketerUserId = localStorage.getItem("MeAsmarketerUserId");
  const isMarketerSession = isActualMarketer();
  const effectiveUserId =
    isMarketerSession &&
    marketerUserId &&
    marketerUserId !== "undefined" &&
    marketerUserId !== "null"
      ? marketerUserId
      : currentUserId;

  // Scope connectivity to the profile you are on:
  // shop page → that shopid; user profile → userid only (personal connections)
  const shopScopeId =
    id && id !== "null" && id !== "undefined" && id !== "" ? id : null;

  const pathName =
    typeof window !== "undefined" ? window.location.pathname || "" : "";
  const isOnUserProfilePage =
    pathName === "/user-profile" || pathName.endsWith("/user-profile");
  const isOnMarketingPage =
    pathName === "/marketing-profile" || pathName.endsWith("/marketing-profile");

  // /user-profile: always personal Connectivity (ignore leftover marketer session)
  const isMarketerPage =
    !isOnUserProfilePage && (isOnMarketingPage || isMarketerSession);

  const connectionQueryArgs = {
    userid: isOnUserProfilePage ? currentUserId : effectiveUserId,
    ...(shopScopeId ? { shopid: shopScopeId } : {}),
    ...(isMarketerPage ? { isMarketerProfile: "true" } : {}),
  };

  const { data: connectedDataResponse } = useGetAllConnectedQuery(
    connectionQueryArgs,
    { skip: !(isOnUserProfilePage ? currentUserId : effectiveUserId) },
  );

  const isInvalidData = !timelineid || !postId;

  const applyBtList = (btValues) => {
    const unique = [...new Set((btValues || []).filter(Boolean))];
    if (!unique.includes("generalPublic") && !shopScopeId) {
      unique.push("generalPublic");
    }

    const dynamicTypes = unique
      .map((bt, index) => {
        const value =
          typeof bt === "string" ? bt : bt?.businessType || bt?.value;
        if (!value) return null;
        return {
          id: index + 1,
          label: formatBtLabel(value),
          value,
          // Do not pre-check anything — user picks General Public OR a
          // connected shop so the post shows only on that chosen area.
          isSelected: false,
        };
      })
      .filter(Boolean);

    if (!dynamicTypes.length) {
      dynamicTypes.push({
        id: 1,
        label: "General Public",
        value: "generalPublic",
        isSelected: !shopScopeId,
      });
    }

    setListsharetoother(dynamicTypes);
    setCheckboxes(dynamicTypes);
    setIsShareDisabled(!dynamicTypes.some((item) => item.isSelected));
  };

  const connectedBtValues = () => {
    const raw =
      connectedDataResponse?.connectedBT ||
      connectedDataResponse?.data?.connectedBT ||
      [];

    return (raw || [])
      .filter((item) => {
        if (typeof item === "string") return true;
        const bt = item?.businessType;
        const count = item?.count || 0;
        const items = Array.isArray(item?.items) ? item.items : [];
        const realItems = items.filter(
          (i) =>
            i &&
            (i.userid || i.shopid || i._id || i._marketerDocId) &&
            !i.isPlaceholder,
        );
        // Only keep connected business types (that have real connections)
        return count > 0 || realItems.length > 0;
      })
      .map((bt) =>
        typeof bt === "string" ? bt : bt?.businessType || bt?.value,
      )
      .filter(Boolean);
  };

  // Load connected BTs for the profile you are on (user profile OR shop).
  // Merge share API + Profile Connectivity so the modal lists every connected BT.
  useEffect(() => {
    const shareUserId = isOnUserProfilePage ? currentUserId : effectiveUserId;
    if (!shareUserId) return;

    let cancelled = false;
    (async () => {
      const fromParent = Array.isArray(sharetoOthers) ? sharetoOthers : [];
      let fromApi = [];

      try {
        const response = await fetchShareConnections({
          ...(shopScopeId ? { shopid: shopScopeId } : {}),
          userid: shareUserId,
          ...(isMarketerPage ? { isMarketerProfile: "true" } : {}),
        }).unwrap();
        if (!cancelled) {
          fromApi = response?.shareto || [];
        }
      } catch (_) {
        /* fall through to connectivity / parent */
      }

      if (cancelled) return;

      const fromConnectivity = connectedBtValues();
      let merged;
      if (isMarketerPage) {
        // On Marketer Profile, ONLY show checkboxes for business types that have connected connections
        merged =
          fromConnectivity.length > 0
            ? fromConnectivity
            : fromApi.length > 0
              ? fromApi
              : ["generalPublic"];
      } else {
        // /user-profile + shop pages: union of share API + Connectivity cards
        // so every connected shop BT and General Public appears in Share on.
        merged = [
          ...new Set(
            [...fromApi, ...fromParent, ...fromConnectivity].filter(Boolean),
          ),
        ];
        if (merged.length === 0) merged = ["generalPublic"];
      }
      applyBtList(merged);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectiveUserId,
    currentUserId,
    isOnUserProfilePage,
    isMarketerPage,
    shopScopeId,
    sharetoOthers,
    connectedDataResponse,
    fetchShareConnections,
  ]);

  const handleSelectAll = (event) => {
    const checked = event.target.checked;

    const mapAll = (items) =>
      items.map((item) => ({ ...item, isSelected: checked }));

    if (isInvalidData) {
      const updatedList = mapAll(listsharetoother);
      setListsharetoother(updatedList);
      setIsShareDisabled(!updatedList.some((item) => item.isSelected));
    } else {
      const updatedCheckboxes = mapAll(checkboxes);
      setCheckboxes(updatedCheckboxes);
      setIsShareDisabled(!updatedCheckboxes.some((item) => item.isSelected));
    }
  };

  const handleSelectSingle = (event) => {
    const checkboxId = parseInt(event.target.value, 10);

    const toggleSingle = (items) =>
      items.map((item) => {
        if (item.id === checkboxId) {
          return { ...item, isSelected: !item.isSelected };
        }
        return item;
      });

    if (isInvalidData) {
      const updatedList = toggleSingle(listsharetoother);
      setListsharetoother(updatedList);
      setIsShareDisabled(!updatedList.some((item) => item.isSelected));
    } else {
      const updatedCheckboxes = toggleSingle(checkboxes);
      setCheckboxes(updatedCheckboxes);
      setIsShareDisabled(!updatedCheckboxes.some((item) => item.isSelected));
    }
  };

  const handleShare = async () => {
    let selectedBusinessTypes;

    if (isInvalidData) {
      selectedBusinessTypes = listsharetoother
        .filter((item) => item.isSelected)
        .map((item) => item.value || item.label);
    } else {
      selectedBusinessTypes = checkboxes
        .filter((item) => item.isSelected)
        .map((item) => item.value || item.label);
    }

    let activeTimelineId = timelineIdMar;
    let activePostId = postIdMar;

    if (
      isInvalidData &&
      !isActualMarketer() &&
      (!activeTimelineId || !activePostId)
    ) {
      const shareUrl = localStorage.getItem("ShareUrl");
      const data = {
        shopid: id || "",
        userid: currentUser?._id,
        businessType: "generalPublic",
        bymarketer: false,
        my: [
          {
            heading: "Shop Item Promotion",
            content: `Click on this link , ${shareUrl}`,
            userid: currentUser?._id,
          },
        ],
      };
      try {
        const timelineRes = await createTimeline(data).unwrap();
        const timeline = timelineRes?.timeline || timelineRes?.data?.timeline;
        if (timeline) {
          activePostId = timeline?.my?.[0];
          activeTimelineId = timeline?._id;
          setPostIdMar(activePostId);
          setTimelineIdMar(activeTimelineId);
        }
      } catch (error) {
        FailedNotify("Failed to prepare post for sharing. Try again.");
        return;
      }
    }

    const shareUserId = (isOnUserProfilePage ? currentUserId : effectiveUserId) || currentUser?._id;

    const resolvedOriginShop =
      originShopId &&
      originShopId !== "null" &&
      originShopId !== "undefined" &&
      originShopId !== ""
        ? originShopId
        : null;

    const selectedShopBTs = (selectedBusinessTypes || []).filter(
      (bt) =>
        bt &&
        bt !== "generalPublic" &&
        bt !== "home" &&
        bt !== "marketerProfile",
    );
    // /user-profile shop share: stamp display BT as the selected shop area
    // (not generalPublic), so sender tab matching works.
    // Never treat /user-profile as a marketer share session.
    const isMarketerShareSession =
      !isOnUserProfilePage &&
      (isMarketerSession ||
        window.location.pathname === "/marketing-profile" ||
        localStorage.getItem("previousRoute") === "marketing-profile");

    const resolvedMyBT = isMarketerShareSession
      ? "marketerProfile"
      : !shopScopeId && selectedShopBTs.length > 0
        ? selectedShopBTs[0]
        : mybusinessType || "generalPublic";

    let requestBody;
    if (isInvalidData) {
      requestBody = {
        shopid: id || null,
        originShopId: resolvedOriginShop || id || null,
        userid: shareUserId,
        businessType: selectedBusinessTypes,
        mybusinessType: resolvedMyBT,
        timelineid: activeTimelineId,
        postid: activePostId,
        asMarketerProfile: isMarketerShareSession,
        fromMarketerProfile: isMarketerShareSession,
      };
    } else {
      requestBody = {
        shopid: id || null,
        originShopId: resolvedOriginShop || id || null,
        userid: shareUserId,
        businessType: selectedBusinessTypes,
        mybusinessType: resolvedMyBT,
        timelineid,
        postid: postId,
        asMarketerProfile: isMarketerShareSession,
        fromMarketerProfile: isMarketerShareSession,
      };
    }

    // From /user-profile or shop: modal BTs pick WHO; display area is SOURCE
    // (mybusinessType / page). Do not force GP into selection.

    if (!requestBody.postid || !requestBody.timelineid) {
      FailedNotify("Missing post or timeline id. Try again.");
      return;
    }

    try {
      const shareResult = await timelineShareMutation(requestBody).unwrap();

      if (shareResult?.alreadyShared) {
        SuccessNotify("Already shared — opening selected area.");
        if (typeof onShareSuccess === "function") {
          const shopOnly = (selectedBusinessTypes || []).filter(
            (bt) => bt && bt !== "generalPublic" && bt !== "home",
          );
          const sourceArea =
            requestBody.mybusinessType || mybusinessType || "generalPublic";
          onShareSuccess(
            !requestBody.shopid
              ? shopOnly.length
                ? shopOnly
                : ["generalPublic"]
              : [sourceArea],
          );
        }
        dispatch(apiSlice.util.invalidateTags(["Timeline", "VisitProfile"]));
        if (typeof refetchTimeline === "function") {
          try {
            await refetchTimeline();
          } catch (_) {
            /* ignore */
          }
        }
        handleCloseshare();
        return;
      }

      SuccessNotify("Post shared successfully");

      const marketingShopId =
        requestBody.shopid ||
        localStorage.getItem("OpenShopIdForMarketingByMarketer");
      const bumpBy = Number(shareResult?.sharedRecipientsThisShare) || 0;
      const absoluteShareCount =
        typeof shareResult?.shareCount === "number"
          ? shareResult.shareCount
          : null;
      if (marketingShopId && bumpBy > 0) {
        dispatch(
          apiSlice.util.updateQueryData(
            "getSubscriptionPurchasesByShop",
            marketingShopId,
            (draft) => {
              const target = draft?.data || draft;
              if (!target || typeof target !== "object") return;
              const next =
                Number(target.profileShares || target.sharedToUsersCount || 0) +
                bumpBy;
              target.profileShares = next;
              target.sharedToUsersCount = next;
            },
          ),
        );
      }

      const sharedPostId = requestBody.postid;
      const senderBT = requestBody.mybusinessType || "generalPublic";
      if (sharedPostId) {
        const patchTimeline = (draft) => {
          if (!draft || !draft.data) return;
          draft.data.forEach((tl) => {
            let postObj = null;
            if (Array.isArray(tl.my)) {
              tl.my = tl.my.map((p) => {
                if ((p?._id || p)?.toString() === String(sharedPostId)) {
                  const nextCount =
                    absoluteShareCount != null
                      ? absoluteShareCount
                      : (p.shareCount || 0) + bumpBy;
                  postObj = {
                    ...p,
                    shareCount: nextCount,
                  };
                  return postObj;
                }
                return p;
              });
            }
            if (!Array.isArray(tl.sharedtoother)) tl.sharedtoother = [];
            // /user-profile: shop BT selected → that shop area tab.
            // GP selected (or no shop) → General Public.
            // Shop page: SOURCE area (FROM) as before.
            const sourceDisplayBT = senderBT || "generalPublic";
            const isPersonShare = !requestBody.shopid;
            const selectedShopBTsPatch = (selectedBusinessTypes || []).filter(
              (bt) => bt && bt !== "generalPublic" && bt !== "home",
            );
            const btsForSenderPatch = isPersonShare
              ? selectedShopBTsPatch.length
                ? selectedShopBTsPatch
                : ["generalPublic"]
              : [sourceDisplayBT];

            // Drop leftover GP stamps for this post when sharing to a shop area
            // (user-profile) OR when sharing FROM a shop page.
            const stripGpForShopShare =
              (isPersonShare &&
                selectedShopBTsPatch.length > 0 &&
                !selectedBusinessTypes?.includes("generalPublic")) ||
              (!isPersonShare &&
                sourceDisplayBT &&
                sourceDisplayBT !== "generalPublic");
            if (stripGpForShopShare) {
              tl.sharedtoother = tl.sharedtoother.filter((s) => {
                const samePost =
                  (s.postid?._id || s.postid)?.toString() ===
                  String(sharedPostId);
                if (!samePost) return true;
                const bt = String(s.businessType || "")
                  .toLowerCase()
                  .replace(/[\s_-]+/g, "");
                const from = String(s.senderBusinessType || "")
                  .toLowerCase()
                  .replace(/[\s_-]+/g, "");
                const isGp =
                  (bt === "generalpublic" || !bt) &&
                  (from === "generalpublic" || !from);
                return !isGp;
              });
            }

            btsForSenderPatch.forEach((bt) => {
              const already = tl.sharedtoother.some(
                (s) =>
                  (s.postid?._id || s.postid)?.toString() ===
                    String(sharedPostId) &&
                  String(s.businessType || "") === String(bt),
              );
              if (already) return;
              tl.sharedtoother.unshift({
                _id: `optimistic-share-${sharedPostId}-${bt}`,
                postid: postObj || {
                  _id: sharedPostId,
                  shareCount:
                    absoluteShareCount != null ? absoluteShareCount : bumpBy,
                },
                businessType: bt,
                senderBusinessType: bt,
                sharebyshopid: requestBody.shopid || null,
                sharebyshopuserid: requestBody.userid,
                sharetoshopuserid: null,
                createdAt: new Date().toISOString(),
              });
            });
            if (postObj) {
              ["sharebyother", "sharedtoother"].forEach((key) => {
                if (!Array.isArray(tl[key])) return;
                tl[key] = tl[key].map((s) =>
                  (s.postid?._id || s.postid)?.toString() ===
                    String(sharedPostId) &&
                  typeof s.postid === "object" &&
                  s.postid
                    ? {
                        ...s,
                        postid: {
                          ...s.postid,
                          shareCount: postObj.shareCount,
                        },
                      }
                    : s,
                );
              });
            }
          });
        };

        const queries = store.getState()?.api?.queries || {};
        Object.keys(queries).forEach((key) => {
          if (!key.startsWith("getTimelinePosts(")) return;
          const queryArgs = queries[key]?.originalArgs;
          if (!queryArgs) return;
          dispatch(
            apiSlice.util.updateQueryData(
              "getTimelinePosts",
              queryArgs,
              patchTimeline,
            ),
          );
        });
      }

      if (typeof onShareSuccess === "function") {
        // /user-profile: shop selection → that area; GP → General Public.
        // Shop-page share → SOURCE shop area only (not modal "who" list).
        const isPersonShare = !requestBody.shopid;
        const shopOnly = (selectedBusinessTypes || []).filter(
          (bt) => bt && bt !== "generalPublic" && bt !== "home",
        );
        const sourceArea =
          requestBody.mybusinessType || mybusinessType || "generalPublic";
        onShareSuccess(
          isPersonShare
            ? shopOnly.length
              ? shopOnly
              : ["generalPublic"]
            : [sourceArea],
        );
      }

      dispatch(apiSlice.util.invalidateTags(["Timeline", "VisitProfile"]));

      if (typeof refetchTimeline === "function") {
        try {
          await refetchTimeline();
        } catch (_) {
          /* optimistic patch already applied */
        }
      }

      handleCloseshare();
    } catch (error) {
      console.error("Error sharing the post:", error);
      FailedNotify("Failed to share post. Try again.");
    }
  };

  const sendFallbackTimeline = async () => {
    if (!isInvalidData) return;

    const marketeruserid = localStorage.getItem("MeAsmarketerUserId");
    const marketerid = localStorage.getItem("MeAsmarketerId");
    const marketerFor = localStorage.getItem("MarketerFor");
    const shareUrl = localStorage.getItem("ShareUrl");

    const data = {
      shopid: "",
      userid: marketeruserid,
      businessType: "generalPublic",
      bymarketer: true,
      marketerid,
      my: [
        {
          marketerid,
          bymarketer: true,
          heading: "Shop Item Promotion",
          content: `Click on this link , ${shareUrl}`,
          img: ["img1.jpg"],
          video: ["video1.mp4"],
          userid: marketeruserid,
        },
      ],
    };

    try {
      const timelineRes = await createTimeline(data).unwrap();
      const timeline = timelineRes?.timeline || timelineRes?.data?.timeline;

      if (timeline) {
        setPostIdMar(timeline?.my?.[0]);
        setTimelineIdMar(timeline?._id);
      }

      if (marketerFor) {
        const rulesRes = await fetchBusinessTypeRules(marketerFor).unwrap();
        const data1 = rulesRes?.data;

        const marketerTypes = data1?.connectwith
          ?.filter((item) => item.marketer === true)
          ?.map((item, index) => ({
            id: index + 1,
            label: item.type,
            isSelected: false,
          }));

        setListsharetoother(marketerTypes || []);
      }
    } catch (error) {
      console.error("Error in fallback timeline flow:", error);
      FailedNotify("Failed to load share options. Try again.");
    }
  };

  return {
    checkboxes,
    isShareDisabled,
    isSharing,
    modalShow,
    setModalShow,
    listsharetoother,
    isInvalidData,
    handleSelectAll,
    handleSelectSingle,
    handleShare,
    sendFallbackTimeline,
  };
};

export default useShareOption;
